import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  FormGroup,
  FormControlLabel,
  Checkbox,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import axios from "axios";

const EventFilterPop = ({
  open,
  filterType,
  onClose,
  onSave,
  selectedValues = [],
}) => {
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOptions, setFilterOptions] = useState({
    location: [],
    organization: [],
    date: { years: [], months: [] },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSelected(selectedValues);
  }, [filterType, selectedValues]);

  useEffect(() => {
    if (open && filterType) {
      fetchFilterOptions(filterType);
    }
  }, [open, filterType]);

  const fetchFilterOptions = async (type) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/events/filters/${type}`, {
        withCredentials: true,
      });

      // Handle the date filter which returns an object with years and months arrays
      if (type === "date") {
        setFilterOptions((prev) => ({
          ...prev,
          date: response.data,
        }));
      } else {
        setFilterOptions((prev) => ({
          ...prev,
          [type]: response.data,
        }));
      }
    } catch (err) {
      console.error(`Failed to fetch ${type} filter options:`, err);
      setError(`Failed to load filter options. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (value) => {
    setSelected((prev) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleSave = () => {
    onSave(filterType, selected);
    onClose();
  };

  const handleSelectAll = () => {
    if (filterType === "date") {
      const allDateOptions = getDateOptions();
      setSelected(
        selected.length === allDateOptions.length ? [] : allDateOptions
      );
    } else {
      const options = filterOptions[filterType] || [];
      setSelected(selected.length === options.length ? [] : [...options]);
    }
  };

  const getDateOptions = () => {
    const months = filterOptions.date?.months || [];
    const years = filterOptions.date?.years || [];
    return months.flatMap((month) => years.map((year) => `${month} ${year}`));
  };

  const getFilterItems = () => {
    if (filterType === "date") {
      const dateOptions = getDateOptions();
      return dateOptions.filter((date) =>
        date.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    const options = filterOptions[filterType] || [];
    return filterType === "location"
      ? options.filter((location) =>
          location?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : options;
  };

  const filteredItems = getFilterItems();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="filter-gallery-popup"
      PaperProps={{
        className: "filter-gallery-paper",
      }}
      aria-labelledby="filter-dialog-title"
      keepMounted={false}
      disablePortal={false}
      disableEnforceFocus={false}
      disableAutoFocus={false}
    >
      <DialogTitle className="filter-gallery-title" id="filter-dialog-title">
        <span>{`Filter by ${filterType}`}</span>
        <Button
          onClick={handleSelectAll}
          sx={{ color: "#81b2ff" }}
          className="filter-gallery-select-all"
        >
          {selected.length === (filteredItems?.length || 0)
            ? "Deselect All"
            : "Select All"}
        </Button>
      </DialogTitle>
      <DialogContent>
        {(filterType === "location" || filterType === "date") && (
          <div className="filter-state-search-container event-filter-search-container">
            <input
              type="text"
              placeholder={`Search ${filterType}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="filter-state-search-input"
              aria-label={`Search ${filterType}`}
            />
          </div>
        )}

        {loading ? (
          <div className="filter-loading">Loading options...</div>
        ) : error ? (
          <div className="filter-error">{error}</div>
        ) : (
          <FormGroup className="filter-gallery-options">
            {filteredItems?.map((item) => (
              <FormControlLabel
                key={item}
                control={
                  <Checkbox
                    checked={selected.includes(item)}
                    onChange={() => handleToggle(item)}
                  />
                }
                label={item}
              />
            ))}
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button sx={{ color: "#fff" }} onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventFilterPop;
